import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import supabase from '../lib/supabase'
import BackgroundSlideshow from './BackgroundSlideshow'

const DEFAULTS = {
  hero_background_image: '/assets/aox-hero-team-2026.webp',
  hero_background_images: ['/assets/aox-hero-team-2026.webp'],
  hero_background_position: '58% center',
  hero_background_position_mobile: '64% center',
  hero_overlay_opacity: 10,
  hero_effects_enabled: true,
  hero_slideshow_enabled: true,
  hero_slideshow_interval: 8000,
  hero_transition_duration: 1400,
  hero_content_box_opacity: 10,
  academy_background_images: [],
  academy_slideshow_enabled: true,
  academy_slideshow_interval: 9000,
  academy_transition_duration: 1500,
  academy_overlay_opacity: 48,
  academy_background_position: 'center center',
  sponsors_background_images: [],
  sponsors_slideshow_enabled: true,
  sponsors_slideshow_interval: 9000,
  sponsors_transition_duration: 1500,
  sponsors_overlay_opacity: 62,
  sponsors_background_position: 'center center',
}

const asImages = (value, fallback = []) => {
  const list = Array.isArray(value)
    ? value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
    : []
  return list.length ? [...new Set(list)] : fallback
}

const clamp = (value, min, max, fallback) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(min, Math.min(max, parsed))
}

const normalizeSettings = (data = {}) => {
  const heroFallback = data.hero_background_image || DEFAULTS.hero_background_image
  return {
    ...DEFAULTS,
    ...data,
    hero_background_image: heroFallback,
    hero_background_images: asImages(data.hero_background_images, [heroFallback]),
    academy_background_images: asImages(data.academy_background_images, []),
    sponsors_background_images: asImages(data.sponsors_background_images, []),
    hero_overlay_opacity: clamp(data.hero_overlay_opacity, 0, 80, DEFAULTS.hero_overlay_opacity),
    hero_content_box_opacity: clamp(
      data.hero_content_box_opacity,
      0,
      60,
      DEFAULTS.hero_content_box_opacity,
    ),
    academy_overlay_opacity: clamp(
      data.academy_overlay_opacity,
      0,
      90,
      DEFAULTS.academy_overlay_opacity,
    ),
    sponsors_overlay_opacity: clamp(
      data.sponsors_overlay_opacity,
      0,
      90,
      DEFAULTS.sponsors_overlay_opacity,
    ),
  }
}

const findHomepageTargets = () => {
  const sections = Array.from(document.querySelectorAll('section'))
  const hero = sections.find(
    (section) =>
      section.classList.contains('relative') &&
      section.classList.contains('flex') &&
      section.classList.contains('items-center') &&
      section.classList.contains('justify-center'),
  ) || null
  const academy = sections.find(
    (section) =>
      section.classList.contains('py-16') &&
      section.classList.contains('from-blue-600') &&
      section.classList.contains('to-blue-700'),
  ) || null
  const sponsors = sections.find(
    (section) =>
      section.classList.contains('from-secondary-900') &&
      section.classList.contains('via-primary-900') &&
      section.classList.contains('to-primary-700'),
  ) || null

  return { hero, academy, sponsors }
}

const HomepageBackgroundManager = () => {
  const location = useLocation()
  const [settings, setSettings] = useState(DEFAULTS)
  const [targets, setTargets] = useState({ hero: null, academy: null, sponsors: null })

  useEffect(() => {
    let cancelled = false

    const loadSettings = async () => {
      const { data, error } = await supabase
        .from('site_config_aox1234')
        .select(
          'hero_background_image,hero_background_images,hero_background_position,hero_background_position_mobile,hero_overlay_opacity,hero_effects_enabled,hero_slideshow_enabled,hero_slideshow_interval,hero_transition_duration,hero_content_box_opacity,academy_background_images,academy_slideshow_enabled,academy_slideshow_interval,academy_transition_duration,academy_overlay_opacity,academy_background_position,sponsors_background_images,sponsors_slideshow_enabled,sponsors_slideshow_interval,sponsors_transition_duration,sponsors_overlay_opacity,sponsors_background_position',
        )
        .limit(1)
        .single()

      if (!cancelled) {
        if (error) {
          if (import.meta.env.DEV) console.error('Homepage background settings error:', error)
          setSettings(DEFAULTS)
        } else {
          setSettings(normalizeSettings(data || {}))
        }
      }
    }

    loadSettings()
    return () => {
      cancelled = true
    }
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname !== '/') {
      setTargets({ hero: null, academy: null, sponsors: null })
      return undefined
    }

    let frame = null
    let tries = 0
    const resolveTargets = () => {
      frame = window.requestAnimationFrame(() => {
        const found = findHomepageTargets()
        setTargets((prev) => {
          if (
            prev.hero === found.hero &&
            prev.academy === found.academy &&
            prev.sponsors === found.sponsors
          ) {
            return prev
          }
          return found
        })

        tries += 1
        if ((!found.hero || !found.academy || !found.sponsors) && tries < 12) {
          window.setTimeout(resolveTargets, 120)
        }
      })
    }

    resolveTargets()
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [location.pathname])

  useEffect(() => {
    const firstHeroImage = settings.hero_background_images[0] || settings.hero_background_image
    const root = document.documentElement

    root.style.setProperty(
      '--aox-hero-bg-image',
      `url(${JSON.stringify(firstHeroImage || DEFAULTS.hero_background_image)})`,
    )
    root.style.setProperty(
      '--aox-hero-bg-position',
      settings.hero_background_position || DEFAULTS.hero_background_position,
    )
    root.style.setProperty(
      '--aox-hero-bg-position-mobile',
      settings.hero_background_position_mobile || DEFAULTS.hero_background_position_mobile,
    )
    root.style.setProperty(
      '--aox-hero-overlay-opacity',
      (clamp(settings.hero_overlay_opacity, 0, 80, 10) / 100).toFixed(2),
    )
    document.body.classList.toggle('aox-hero-effects-off', settings.hero_effects_enabled === false)
  }, [settings])

  useEffect(() => {
    if (targets.hero) {
      targets.hero.style.position = 'relative'
      targets.hero.style.overflow = 'hidden'
      targets.hero.style.setProperty('background-image', 'none', 'important')

      const legacyLayers = targets.hero.querySelector(':scope > .absolute.inset-0')
      if (legacyLayers) {
        legacyLayers.style.zIndex = '1'

        const legacyImageLayer = legacyLayers.querySelector(':scope > .absolute.inset-0.bg-cover')
        if (legacyImageLayer) {
          legacyImageLayer.style.display = 'none'
        }
      }

      const contentBox = targets.hero.querySelector('.max-w-3xl.mx-auto')
      if (contentBox) {
        contentBox.style.backgroundColor = `rgba(0, 0, 0, ${
          clamp(settings.hero_content_box_opacity, 0, 60, 10) / 100
        })`
      }
    }

    if (targets.academy) {
      targets.academy.style.position = 'relative'
      targets.academy.style.overflow = 'hidden'
      const academyContent = targets.academy.querySelector(':scope > div')
      if (academyContent) {
        academyContent.style.position = 'relative'
        academyContent.style.zIndex = '10'
      }
    }

    if (targets.sponsors) {
      targets.sponsors.style.position = 'relative'
      targets.sponsors.style.overflow = 'hidden'
    }
  }, [targets, settings.hero_content_box_opacity])

  const heroImages = useMemo(
    () => asImages(settings.hero_background_images, [settings.hero_background_image]),
    [settings.hero_background_images, settings.hero_background_image],
  )

  if (location.pathname !== '/') return null

  return (
    <>
      {targets.hero &&
        createPortal(
          <BackgroundSlideshow
            images={heroImages}
            fallbackImage={settings.hero_background_image || DEFAULTS.hero_background_image}
            enabled={settings.hero_slideshow_enabled !== false}
            interval={settings.hero_slideshow_interval}
            transitionDuration={settings.hero_transition_duration}
            position={settings.hero_background_position}
            positionMobile={settings.hero_background_position_mobile}
            overlayOpacity={0}
          />,
          targets.hero,
        )}

      {targets.academy && settings.academy_background_images.length > 0 &&
        createPortal(
          <BackgroundSlideshow
            images={settings.academy_background_images}
            enabled={settings.academy_slideshow_enabled !== false}
            interval={settings.academy_slideshow_interval}
            transitionDuration={settings.academy_transition_duration}
            position={settings.academy_background_position}
            overlayOpacity={settings.academy_overlay_opacity}
          />,
          targets.academy,
        )}

      {targets.sponsors && settings.sponsors_background_images.length > 0 &&
        createPortal(
          <BackgroundSlideshow
            images={settings.sponsors_background_images}
            enabled={settings.sponsors_slideshow_enabled !== false}
            interval={settings.sponsors_slideshow_interval}
            transitionDuration={settings.sponsors_transition_duration}
            position={settings.sponsors_background_position}
            overlayOpacity={settings.sponsors_overlay_opacity}
          />,
          targets.sponsors,
        )}
    </>
  )
}

export default HomepageBackgroundManager
